import Gantt from "frappe-gantt";
import date_utils from "frappe-gantt/src/date_utils";
import {createSVG} from "frappe-gantt/src/svg_utils";

/**
 * Adds a 'quarter' view to the gantt
 *
 * @param {GanttWrapper.GanttWrapper} ganttInstance
 */
function addQuarterViewMode(ganttInstance) {
	// Update global view mode object
	Gantt.VIEW_MODE.QUARTER = 'Quarter';

	const VIEW_MODE = Gantt.VIEW_MODE;

	// Update in instance
	ganttInstance.options.view_modes.push('Quarter');

	/**
	 * Update internal functions to support new view mode
	 */

	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.update_view_scale = function (view_mode) {
		this.options.view_mode = view_mode;

		if (view_mode === VIEW_MODE.DAY) {
			this.options.step = 24;
			this.options.column_width = 38;
		} else if (view_mode === VIEW_MODE.HALF_DAY) {
			this.options.step = 24 / 2;
			this.options.column_width = 38;
		} else if (view_mode === VIEW_MODE.QUARTER_DAY) {
			this.options.step = 24 / 4;
			this.options.column_width = 38;
		} else if (view_mode === VIEW_MODE.WEEK) {
			this.options.step = 24 * 7;
			this.options.column_width = 140;
		} else if (view_mode === VIEW_MODE.MONTH) {
			this.options.step = 24 * 30;
			this.options.column_width = 120;
		} else if (view_mode === VIEW_MODE.YEAR) {
			this.options.step = 24 * 365;
			this.options.column_width = 120;
		} else if (view_mode === VIEW_MODE.QUARTER) {
			this.options.step = 24 * 30;
			this.options.column_width = 60;
		}
	}

	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.setup_gantt_dates = function () {
		this.gantt_start = this.gantt_end = null;

		for (let task of this.tasks) {
			// set global start and end date
			if (!this.gantt_start || task._start < this.gantt_start) {
				this.gantt_start = task._start;
			}
			if (!this.gantt_end || task._end > this.gantt_end) {
				this.gantt_end = task._end;
			}
		}

		this.gantt_start = date_utils.start_of(this.gantt_start, 'day');
		this.gantt_end = date_utils.start_of(this.gantt_end, 'day');

		// add date padding on both sides
		if (this.view_is([VIEW_MODE.QUARTER_DAY, VIEW_MODE.HALF_DAY])) {
			this.gantt_start = date_utils.add(this.gantt_start, -7, 'day');
			this.gantt_end = date_utils.add(this.gantt_end, 7, 'day');
		} else if (this.view_is([VIEW_MODE.MONTH, VIEW_MODE.QUARTER])) {
			this.gantt_start = date_utils.start_of(this.gantt_start, 'year');
			this.gantt_end = date_utils.add(this.gantt_end, 1, 'year');
		} else if (this.view_is(VIEW_MODE.YEAR)) {
			this.gantt_start = date_utils.add(this.gantt_start, -2, 'year');
			this.gantt_end = date_utils.add(this.gantt_end, 2, 'year');
		} else {
			this.gantt_start = date_utils.add(this.gantt_start, -1, 'month');
			this.gantt_end = date_utils.add(this.gantt_end, 1, 'month');
		}
	}

	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.setup_date_values = function () {
		this.dates = [];
		let cur_date = null;

		while (cur_date === null || cur_date < this.gantt_end) {
			if (!cur_date) {
				cur_date = date_utils.clone(this.gantt_start);
			} else {
				if (this.view_is(VIEW_MODE.YEAR)) {
					cur_date = date_utils.add(cur_date, 1, 'year');
				} else if (this.view_is([VIEW_MODE.MONTH, VIEW_MODE.QUARTER])) {
					cur_date = date_utils.add(cur_date, 1, 'month');
				} else {
					cur_date = date_utils.add(
						cur_date,
						this.options.step,
						'hour'
					);
				}
			}
			this.dates.push(cur_date);
		}
	}

	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.make_grid_ticks = function () {
		let tick_x = 0;
		let tick_y = this.options.header_height + this.options.padding / 2;
		let tick_height =
			(this.options.bar_height + this.options.padding) *
			this.tasks.length;

		for (let date of this.dates) {
			let tick_class = 'tick';
			// thick tick for monday
			if (this.view_is(VIEW_MODE.DAY) && date.getDate() === 1) {
				tick_class += ' thick';
			}
			// thick tick for first week
			if (
				this.view_is(VIEW_MODE.WEEK) &&
				date.getDate() >= 1 &&
				date.getDate() < 8
			) {
				tick_class += ' thick';
			}
			// thick ticks for quarters
			if (
				this.view_is([VIEW_MODE.MONTH, VIEW_MODE.QUARTER]) &&
				(date.getMonth()) % 3 === 0
			) {
				tick_class += ' thick';
			}

			createSVG('path', {
				d: `M ${tick_x} ${tick_y} v ${tick_height}`,
				class: tick_class,
				append_to: this.layers.grid,
			});

			if (this.view_is([VIEW_MODE.MONTH, VIEW_MODE.QUARTER])) {
				tick_x +=
					(date_utils.get_days_in_month(date) *
						this.options.column_width) /
					30;
			} else {
				tick_x += this.options.column_width;
			}
		}
	}

	// Left for documentation purposes
	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.make_grid_highlights = function () {
		// highlight today's date
		if (this.view_is(VIEW_MODE.DAY)) {
			const x =
				(date_utils.diff(date_utils.today(), this.gantt_start, 'hour') /
					this.options.step) *
				this.options.column_width;
			const y = 0;

			const width = this.options.column_width;
			const height =
				(this.options.bar_height + this.options.padding) *
				this.tasks.length +
				this.options.header_height +
				this.options.padding / 2;

			createSVG('rect', {
				x,
				y,
				width,
				height,
				class: 'today-highlight',
				append_to: this.layers.grid,
			});
		} else if (this.view_is(VIEW_MODE.QUARTER)) {
			// We need to find the first and last tick for the related quarter for the given date
			let tick_x = 0;
			let tick_x_end = 0;

			// Calculate current quarter/year string pair
			const today = date_utils.today();
			const currentQuarter = Math.floor((today.getMonth() + 3) / 3);
			const currentQuarterYear = `${date_utils.today().getFullYear()}.${currentQuarter}`;

			// We use this to find both the first tick for the quarter, and the first
			// tick of the next one
			let foundFirstTick = false;

			for (let date of this.dates) {
				const quarter = Math.floor((date.getMonth() + 3) / 3);
				const quarterYear = `${date.getFullYear()}.${quarter}`;

				if (currentQuarterYear === quarterYear && foundFirstTick === false) {
					// Found the first tick for the current quarter, save its x position in tick_x
					foundFirstTick = true;
				}
				if (foundFirstTick && currentQuarterYear !== quarterYear) {
					// We found the first tick of the next quarter, save its x position in tick_x_end
					break;
				}

				const deltaX = (date_utils.get_days_in_month(date) *
						this.options.column_width) /
					30;

				if (foundFirstTick === false) {
					tick_x += deltaX
				}
				tick_x_end += deltaX;
			}

			const x = tick_x;
			const y = 0;

			const width = tick_x_end - tick_x;
			const height =
				(this.options.bar_height + this.options.padding) *
				this.tasks.length +
				this.options.header_height +
				this.options.padding / 2;

			createSVG('rect', {
				x,
				y,
				width,
				height,
				class: 'today-highlight',
				append_to: this.layers.grid,
			});
		}
	}

	ganttInstance.get_snap_position = function (dx) {
		let odx = dx,
			rem,
			position;

		if (this.view_is(VIEW_MODE.WEEK)) {
			rem = dx % (this.options.column_width / 7);
			position =
				odx -
				rem +
				(rem < this.options.column_width / 14
					? 0
					: this.options.column_width / 7);
		} else if (this.view_is([VIEW_MODE.MONTH, VIEW_MODE.QUARTER])) {
			rem = dx % (this.options.column_width / 30);
			position =
				odx -
				rem +
				(rem < this.options.column_width / 60
					? 0
					: this.options.column_width / 30);
		} else {
			rem = dx % this.options.column_width;
			position =
				odx -
				rem +
				(rem < this.options.column_width / 2
					? 0
					: this.options.column_width);
		}
		return position;
	}

	// noinspection JSUndefinedPropertyAssignment
	ganttInstance.get_date_info = function get_date_info(date, last_date, i) {
		if (!last_date) {
			last_date = date_utils.add(date, 1, 'year');
		}

		const monthShortNamesMap = {
			'January': 'Jan',
			'February': 'Feb',
			'March': 'Mar',
			'April': 'Apr',
			'May': 'May',
			'June': 'Jun',
			'July': 'Jul',
			'August': 'Aug',
			'September': 'Sep',
			'October': 'Oct',
			'November': 'Nov',
			'December': 'Dev',
		}

		// Maps months to their quarter headers
		const monthsQuartersMap = {
			'January': '',
			'February': 'Q1',
			'March': '',
			'April': '',
			'May': 'Q2',
			'June': '',
			'July': '',
			'August': 'Q3',
			'September': '',
			'October': '',
			'November': 'Q4',
			'December': '',
		}

		const date_text = {
			'Quarter Day_lower': date_utils.format(
				date,
				'HH',
				this.options.language
			),
			'Half Day_lower': date_utils.format(
				date,
				'HH',
				this.options.language
			),
			Day_lower:
				date.getDate() !== last_date.getDate()
					? date_utils.format(date, 'D', this.options.language)
					: '',
			Week_lower:
				date.getMonth() !== last_date.getMonth()
					? date_utils.format(date, 'D MMM', this.options.language)
					: date_utils.format(date, 'D', this.options.language),
			Month_lower: date_utils.format(date, 'MMMM', this.options.language),
			Quarter_lower: monthShortNamesMap[date_utils.format(date, 'MMMM', this.options.language)],
			Year_lower: date_utils.format(date, 'YYYY', this.options.language),
			'Quarter Day_upper':
				date.getDate() !== last_date.getDate()
					? date_utils.format(date, 'D MMM', this.options.language)
					: '',
			'Half Day_upper':
				date.getDate() !== last_date.getDate()
					? date.getMonth() !== last_date.getMonth()
						? date_utils.format(
							date,
							'D MMM',
							this.options.language
						)
						: date_utils.format(date, 'D', this.options.language)
					: '',
			Day_upper:
				date.getMonth() !== last_date.getMonth()
					? date_utils.format(date, 'MMMM', this.options.language)
					: '',
			Week_upper:
				date.getMonth() !== last_date.getMonth()
					? date_utils.format(date, 'MMMM', this.options.language)
					: '',
			Month_upper:
				date.getFullYear() !== last_date.getFullYear()
					? date_utils.format(date, 'YYYY', this.options.language)
					: '',
			Quarter_upper: monthsQuartersMap[date_utils.format(date, 'MMMM', this.options.language)],
			Year_upper:
				date.getFullYear() !== last_date.getFullYear()
					? date_utils.format(date, 'YYYY', this.options.language)
					: '',
		};

		const base_pos = {
			x: i * this.options.column_width,
			lower_y: this.options.header_height,
			upper_y: this.options.header_height - 25,
		};

		const x_pos = {
			'Quarter Day_lower': (this.options.column_width * 4) / 2,
			'Quarter Day_upper': 0,
			'Half Day_lower': (this.options.column_width * 2) / 2,
			'Half Day_upper': 0,
			Day_lower: this.options.column_width / 2,
			Day_upper: (this.options.column_width * 30) / 2,
			Week_lower: 0,
			Week_upper: (this.options.column_width * 4) / 2,
			Month_lower: this.options.column_width / 2,
			Month_upper: (this.options.column_width * 12) / 2,
			Quarter_lower: this.options.column_width / 2,
			Quarter_upper: this.options.column_width / 2,
			Year_lower: this.options.column_width / 2,
			Year_upper: (this.options.column_width * 30) / 2,
		};

		return {
			upper_text: date_text[`${this.options.view_mode}_upper`],
			lower_text: date_text[`${this.options.view_mode}_lower`],
			upper_x: base_pos.x + x_pos[`${this.options.view_mode}_upper`],
			upper_y: base_pos.upper_y,
			lower_x: base_pos.x + x_pos[`${this.options.view_mode}_lower`],
			lower_y: base_pos.lower_y,
		};
	}
}

export {addQuarterViewMode}
